import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import RequestQuoteModal from "./RequestQuoteModal";

const primaryLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/products", label: "Spices" },
  { to: "/makhana", label: "Makhana" },
  { to: "/pulses", label: "Pulses" },
  { to: "/quality", label: "Quality" },
  { to: "/packaging", label: "Packaging" },
  { to: "/private-label", label: "Private Label" },
  { to: "/export-markets", label: "Global Markets" },
  { to: "/certifications", label: "Certifications" },
];

const moreLinks = [
  { to: "/resources", label: "Resources" },
  { to: "/blog", label: "Journal" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMoreOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
          scrolled
            ? "bg-brand-ivory/90 backdrop-blur-xl border-b border-brand-charcoal/10"
            : "bg-brand-ink/25 backdrop-blur-sm"
        }`}
      >
        <div className="container-luxe flex items-center justify-between h-20">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
            <img
              src="/images/logo/logo.png"
              alt="DeepRoots Spice"
              className="w-14 h-14 object-contain"
            />
            <div className="leading-none">
              <div className={`font-serif text-[19px] tracking-tight transition-colors ${scrolled ? "text-brand-charcoal" : "text-brand-ivory"}`}>DeepRoots Spice</div>
              <div className={`text-[10px] tracking-[0.28em] uppercase mt-1 transition-colors ${scrolled ? "text-brand-charcoal/50" : "text-brand-ivory/60"}`}>India · Since Origin</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {primaryLinks.slice(0, 5).map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[13px] tracking-[0.14em] uppercase font-medium transition-colors link-underline ${
                    isActive
                      ? (scrolled ? "text-brand-green" : "text-brand-gold")
                      : (scrolled ? "text-brand-charcoal/80 hover:text-brand-green" : "text-brand-ivory/85 hover:text-brand-gold")
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <div
              className="relative"
              onMouseEnter={() => setMoreOpen(true)}
              onMouseLeave={() => setMoreOpen(false)}
            >
              <button
                data-testid="nav-more"
                className={`flex items-center gap-1 text-[13px] tracking-[0.14em] uppercase font-medium transition-colors ${scrolled ? "text-brand-charcoal/80 hover:text-brand-green" : "text-brand-ivory/85 hover:text-brand-gold"}`}
              >
                More <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {moreOpen && (
                <div className="absolute right-0 top-full pt-4">
                  <div className="w-56 bg-brand-ivory border border-brand-charcoal/10 shadow-xl py-3">
                    {[...primaryLinks.slice(5), ...moreLinks].map((l) => (
                      <Link
                        key={l.to}
                        to={l.to}
                        data-testid={`nav-more-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className="block px-5 py-2.5 text-[13px] tracking-wide uppercase text-brand-charcoal/80 hover:text-brand-green hover:bg-brand-ivory-warm transition-colors"
                      >
                        {l.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setQuoteOpen(true)}
              data-testid="nav-request-quote"
              className="hidden md:inline-flex px-5 py-2.5 bg-brand-green text-brand-ivory text-[11px] tracking-[0.22em] uppercase font-semibold hover:bg-brand-green-deep transition-colors border border-brand-green hover:border-brand-gold"
            >
              Request Quote
            </button>
            <button
              onClick={() => setOpen((s) => !s)}
              className={`lg:hidden w-10 h-10 flex items-center justify-center ${scrolled ? "text-brand-charcoal" : "text-brand-ivory"}`}
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden bg-brand-ivory border-t border-brand-charcoal/10">
            <div className="container-luxe py-6 flex flex-col divide-y divide-brand-charcoal/5">
              {[...primaryLinks, ...moreLinks].map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="py-3 text-sm tracking-[0.16em] uppercase text-brand-charcoal/80 hover:text-brand-green"
                >
                  {l.label}
                </Link>
              ))}
              <button
                onClick={() => { setQuoteOpen(true); setOpen(false); }}
                className="mt-4 luxe-btn-primary"
                data-testid="nav-mobile-request-quote"
              >
                Request Quote
              </button>
            </div>
          </div>
        )}
      </header>

      <RequestQuoteModal open={quoteOpen} onClose={() => setQuoteOpen(false)} />
    </>
  );

}
